/**
 * StartFight - the fuller door into Combat, for whoever directs the table.
 *
 * A word in the header while the table is somewhere a fight can break out
 * of, and the dialog it opens: where the sides stand, whether a running
 * order is kept or restarted (Keep by default - 2026-10-28), whether
 * initiative is rolled again, and who is surprised. Pressing the Combat plate is still
 * the plain switch; this is for when those three want deciding at once.
 *
 * Lit plates for one-of-several, a rocker for on/off. Placing the sides
 * apart and surprising one of them both need the party to be named.
 */
import Rocker from './controls/Rocker';
import { useState } from 'react';
import common from '../styles/common.module.css';
import styles from '../styles/StartFight.module.css';
import useFocusTrap from '../hooks/useFocusTrap.js';
import useModalBackdrop from '../hooks/useModalBackdrop';
import useEscapeKey from '../hooks/useEscapeKey.js';
import { openingPlan, sitsOutUnder } from '../engine/phase/startFight';

const PLACEMENT_LABELS = [
  ['asTheyAre', 'As they are'],
  ['apart', 'Two sides apart'],
  ['byHand', 'I will place them'],
];
const ORDER_LABELS = [
  ['keep', 'Keep it'],
  ['restart', 'Restart'],
];
const SURPRISE_LABELS = [
  ['nobody', 'Nobody'],
  ['opposition', 'The opposition'],
  ['party', 'The party'],
];
/** The choices that need two sides to mean anything. */
const NEEDS_SIDES = new Set(['apart', 'opposition', 'party']);

/** One of several, as lit plates. */
function Plates({ label, name, options, value, disabled, onPick }) {
  return (
    <div className={styles.plates} role="group" aria-label={label}>
      {options.map(([key, text]) => (
        <button
          key={key}
          type="button"
          className={styles.plate}
          aria-pressed={value === key}
          disabled={disabled(key)}
          data-testid={`fight-${name}-${key}`}
          onClick={() => onPick(key)}
        >
          {text}
        </button>
      ))}
    </div>
  );
}

/** Who sits out round one, in a sentence. */
function surpriseLine(plan, sides) {
  const out = new Set(sitsOutUnder(plan, sides));
  if (out.size === 0) return 'Everyone acts in round one.';
  const names = [...sides.party, ...sides.opposition].filter((c) => out.has(c.id)).map((c) => c.name);
  return `${names.join(', ')} ${names.length === 1 ? 'sits' : 'sit'} out round one.`;
}

/** Where the sides stand, with the distance when they are placed apart. */
function PlacementSection({ plan, set, disabled, twoSides }) {
  return (
    <section className={styles.section}>
      <h4 className={styles.caption}>Where they stand</h4>
      <Plates
        label="Where they stand"
        name="placement"
        options={PLACEMENT_LABELS}
        value={plan.placement}
        disabled={disabled}
        onPick={(placement) => set({ placement })}
      />
      {plan.placement === 'apart' && (
        <label className={styles.distance}>
          Metres between the sides
          <input
            className={common.input}
            type="number"
            min="1"
            data-testid="fight-distance"
            value={plan.distance}
            onChange={(e) => set({ distance: Math.max(1, Number(e.target.value) || 1) })}
          />
        </label>
      )}
      {!twoSides && (
        <p className={styles.note} data-testid="fight-needs-sides">
          Name the party (Sides) to place two sides apart or to surprise one of them.
        </p>
      )}
    </section>
  );
}

/**
 * The order: kept as it runs, or restarted - asked only when one is running (`sides.orderRunning`). Whether everybody
 * rolls initiative again is a restart's question.
 */
function InitiativeSection({ plan, set, running }) {
  const keeping = running && plan.keepOrder;
  return (
    <section className={styles.section}>
      <h4 className={styles.caption}>{running ? 'The turn order' : 'Initiative'}</h4>
      {running && (
        <Plates
          label="The turn order"
          name="order"
          options={ORDER_LABELS}
          value={plan.keepOrder ? 'keep' : 'restart'}
          disabled={() => false}
          onPick={(key) => set({ keepOrder: key === 'keep' })}
        />
      )}
      {keeping ? (
        <p className={styles.note} data-testid="fight-order-line">
          The round and whose turn it is stay; whoever is not in the order yet joins it.
        </p>
      ) : (
        <Rocker
          label="Roll again for everyone"
          testId="fight-reroll"
          on={plan.rerollInitiative}
          onToggle={() => set({ rerollInitiative: !plan.rerollInitiative })}
        />
      )}
    </section>
  );
}

/** Who sits out round one. */
function SurpriseSection({ plan, set, disabled, sides }) {
  return (
    <section className={styles.section}>
      <h4 className={styles.caption}>Who is surprised</h4>
      <Plates
        label="Who is surprised"
        name="surprised"
        options={SURPRISE_LABELS}
        value={plan.surprised}
        disabled={disabled}
        onPick={(surprised) => set({ surprised })}
      />
      <p className={styles.note} data-testid="fight-surprise-line">
        {surpriseLine(plan, sides)}
      </p>
    </section>
  );
}

function FightForm({ sides, plan, setPlan }) {
  const twoSides = sides.party.length > 0 && sides.opposition.length > 0;
  const disabled = (key) => NEEDS_SIDES.has(key) && !twoSides;
  const set = (patch) => setPlan({ ...plan, ...patch });
  return (
    <>
      <PlacementSection plan={plan} set={set} disabled={disabled} twoSides={twoSides} />
      <InitiativeSection plan={plan} set={set} running={!!sides.orderRunning} />
      <SurpriseSection plan={plan} set={set} disabled={disabled} sides={sides} />
    </>
  );
}

function FightDialog({ sides, onStart, onClose }) {
  const trapRef = useFocusTrap(true);
  const backdrop = useModalBackdrop(onClose, trapRef);
  useEscapeKey(true, onClose); // Escape cancels, as a click outside does (2026-10-23).
  // Opens on the opposition surprised when the scene caught them unaware (Stealth) - the GM may change it.
  const [plan, setPlan] = useState(() => openingPlan(sides));
  return (
    <div className={common.modal} onClick={backdrop.onBackdropClick} role="presentation">
      <div
        className={common.modalContent}
        ref={backdrop.boxRef}
        role="dialog"
        aria-modal="true"
        aria-label="Start the fight"
        data-testid="start-fight-dialog"
      >
        <h3 className={styles.title}>Start the fight</h3>
        <FightForm sides={sides} plan={plan} setPlan={setPlan} />
        <div className={styles.footer}>
          <button type="button" className={common.btnSecondary} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={common.btnPrimary}
            data-testid="start-fight-go"
            onClick={() => {
              onStart(plan);
              onClose();
            }}
          >
            Start the fight
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * @param {object} props
 * @param {import('../engine/phase/sides').Sides} props.sides
 * @param {(plan: import('../engine/phase/startFight').FightPlan) => void} props.onStart
 * @param {string} props.className - the header's word class, so it reads as one of the header's controls
 */
export default function StartFight({ sides, onStart, className, testId = 'start-fight' }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className={className} data-testid={testId} onClick={() => setOpen(true)}>
        Start the fight
      </button>
      {open && <FightDialog sides={sides} onStart={onStart} onClose={() => setOpen(false)} />}
    </>
  );
}
